import React, { useRef, useState } from 'react'
import o from '../../components/Overlay.module.css'
import styles from '../BatchesPage.module.css'
import { apiJson } from '../../lib/api'
import { useChanged, useLayer } from '../../components/layers'
import { PendingButton, inertClass, useSlow, Failed } from '../../components/pending'
import { overCountLine, type Batch, type Settings } from './model'

// The Batches cog (V4 bxCog): the tenant's expected maximum bin weight and
// whether passing it is flagged. Blank means no limit, so nothing is flagged;
// the design has no default and none is invented here.
export function SettingsModal({
  user,
  settings,
  batches,
  onClose,
  onSaved,
}: {
  user: any
  settings: Settings
  batches: Batch[]
  onClose: () => void
  onSaved: (message: string) => void
}) {
  const [max, setMax] = useState(settings.max_bin_kg === null ? '' : String(settings.max_bin_kg))
  const [warn, setWarn] = useState(settings.overweight_warnings)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // A layer (h 425-458): Escape, the scrim and Cancel ask first once a value has changed.
  const panelEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged({ max, warn })
  const { requestClose, confirm } = useLayer(true, onClose, { dirty: dirty && !busy, el: panelEl, modal: true, focusFirst: true, busy })
  const slow = useSlow(busy)

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      const s = await apiJson<Settings>(user, '/api/batches/settings', {
        body: { max_bin_kg: max.trim() === '' ? null : max.trim(), overweight_warnings: warn },
      })
      onSaved(
        s.max_bin_kg === null
          ? 'No maximum bin weight set; nothing is flagged'
          : s.overweight_warnings
            ? 'Warning when a bin passes ' + s.max_bin_kg + ' kg'
            : 'Overweight warnings turned off',
      )
    } catch (e: any) {
      setError(e?.message || 'Could not save the batch settings')
      setBusy(false)
    }
  }

  return (
    <div className={o.scrim} onClick={requestClose}>
      <div ref={panelEl} className={[o.modal, o.narrow].join(' ')} role="dialog" aria-modal="true" aria-label="Batch settings" onClick={(e) => e.stopPropagation()}>
        <div className={o.head}>
          <div className={o.headText}>
            <h2 className={o.title}>Batch settings</h2>
            <p className={o.sub}>These apply to every batch in this subscription.</p>
          </div>
          <button type="button" className={o.close} aria-label="Close" onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className={`${o.body} ${inertClass(busy, slow)}`}>
          <div className={o.field}>
            <label className={o.fieldLabel}>Expected maximum bin weight</label>
            <div className={styles.kgField}>
              <input
                className={o.input}
                inputMode="decimal"
                aria-label="Expected maximum bin weight"
                value={max}
                onChange={(e) => setMax(e.target.value.replace(/[^0-9.]/g, ''))}
              />
              <span style={{ fontSize: '0.8438rem', color: 'var(--dp-muted)' }}>kg</span>
            </div>
          </div>
          <div className={o.fieldHint} data-testid="over-count">
            Set it to the weight a full bin of this product should not pass. Over that, staff are likely overfilling and crushing fruit at the bottom of the bin.{' '}
            {max.trim() ? overCountLine(batches, max) + '.' : 'Leave it empty and no bin is flagged.'}
          </div>
          <div className={o.toggleRow}>
            <div className={o.toggleText}>
              <div className={o.toggleTitle}>Show overweight warnings</div>
              <div className={o.toggleSub}>Off means the weight is still recorded, just never flagged.</div>
            </div>
            <button type="button" role="switch" aria-checked={warn} aria-label="Show overweight warnings" className={o.switch} onClick={() => setWarn(!warn)} />
          </div>
        </div>
        <div className={o.foot}>
          <button type="button" className={o.ghost} disabled={busy} onClick={requestClose}>
            Cancel
          </button>
          <PendingButton className={o.primary} busy={busy} pending="Saving…" onClick={save}>
            Save settings
          </PendingButton>
          <Failed text={error} />
        </div>
      </div>
      {confirm}
    </div>
  )
}
